import { useEffect, useMemo, useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Divider,
    FormControlLabel,
    IconButton,
    List,
    ListItemButton,
    MenuItem,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
    LinearProgress,
} from '@mui/material';
import { Title, useNotify } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';

import {
    examTaskCategories,
    examTaskTypes,
    getExamTaskTypeLabel,
    getExamTaskTypesByExamName,
    type ExamName,
    type ExamTaskCategory,
} from '../examTasks/examTaskTypes';
import { SegmentedTabs } from '../admin/SegmentedTabs';
import { formatApiDateTime } from '../admin/dateTimeUtils';
import { SemanticContentScoringPoliciesPanel } from './SemanticContentScoringPoliciesPanel';
import {
    appGrammarMetricKinds,
    createDefaultMarkingComponentConfig,
    createDefaultMockTestScoringConfiguration,
    createDefaultMockTestSectionAllocationConfig,
    createDefaultObjectiveMarkingConfig,
    createDefaultSpeechAnalysisRubricConfig,
    createDefaultTaskMarkingConfig,
    getAdminMarkingTaskConfigs,
    getAdminMockTestScoringConfig,
    markTitleValues,
    markingComponentRuleKinds,
    markingTaskRuleKinds,
    type MarkTitleValue,
    relativeThresholdKinds,
    type RelativeThresholdScoreBand,
    upsertAdminMarkingTaskConfig,
    upsertAdminMockTestScoringConfig,
    type AdminMockTestScoringConfigDTO,
    type AdminTaskMarkingConfigDTO,
    type AppGrammarMetricKind,
    type MarkingComponentConfig,
    type MarkingComponentRuleKind,
    type MarkingTaskRuleKind,
    type MockTestScoringConfiguration,
    type MockTestSectionAllocationConfig,
    type ObjectiveMarkingConfig,
    type RelativeThresholdKind,
    type SpeechAnalysisRubricConfig,
    type TaskMarkingConfig,
    type ThresholdScoreBand,
    type WordCountScoreBand,
} from './markingConfigurationApi';

type ExamTaskTypeOption = (typeof examTaskTypes)[number];
type MarkingSection = 'task-configs' | 'mock-test-scoring' | 'content-scoring';

type OptionItem = {
    value: string;
    label: string;
};

type SummaryCardProps = {
    label: string;
    value: string;
    helper: string;
};

const markingSections: MarkingSection[] = ['task-configs', 'mock-test-scoring', 'content-scoring'];
const sectionLabels: Record<MarkingSection, string> = {
    'task-configs': 'Task Configs',
    'mock-test-scoring': 'Mock Test Scoring',
    'content-scoring': 'Content Scoring',
};

const examNameOptions: Array<{ value: ExamName; label: string }> = [
    { value: 'Academic', label: 'Academic' },
    { value: 'Core', label: 'Core' },
];
const mockTestSectionAllocationTarget = 80;
const mockTestSectionAllocationTolerance = 0.0001;

const markTitleOptions: OptionItem[] = markTitleValues.map((value) => ({
    value,
    label: formatEnumLabel(value),
}));

const markingTaskRuleKindOptions: OptionItem[] = markingTaskRuleKinds.map((value) => ({
    value,
    label: formatEnumLabel(value),
}));

const markingComponentRuleKindOptions: OptionItem[] = markingComponentRuleKinds.map((value) => ({
    value,
    label: formatEnumLabel(value),
}));

const relativeThresholdKindOptions: OptionItem[] = relativeThresholdKinds.map((value) => ({
    value,
    label: formatEnumLabel(value),
}));

const appGrammarMetricKindOptions: OptionItem[] = appGrammarMetricKinds.map((value) => ({
    value,
    label: formatEnumLabel(value),
}));

function cloneValue<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
}

function formatEnumLabel(value: string): string {
    return value
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim();
}

function formatDateTime(value: string): string {
    return formatApiDateTime(value);
}

function getMarkingSection(value: string | undefined): MarkingSection {
    return markingSections.includes(value as MarkingSection) ? (value as MarkingSection) : 'task-configs';
}

function taskKey(examName: ExamName, examTaskType: string): string {
    return `${examName}:${examTaskType}`;
}

function getDefaultRuleKindForCategory(category: ExamTaskCategory): MarkingTaskRuleKind {
    return category === 'Speaking' || category === 'Writing' ? 'ComponentBased' : 'Objective';
}

function createDefaultMockTestDraft(): MockTestScoringConfiguration {
    const sectionAllocationRules = examNameOptions.flatMap(({ value: examName }) =>
        examTaskCategories.map((category) => {
            const firstTaskType =
                getExamTaskTypesByExamName(examName).find((item) => item.category === category.id)?.apiType ?? '';
            return createDefaultMockTestSectionAllocationConfig(examName, category.id, firstTaskType);
        }),
    );

    return createDefaultMockTestScoringConfiguration(sectionAllocationRules);
}

function scopeLegacyMockTestRules(configuration: MockTestScoringConfiguration): MockTestScoringConfiguration {
    const scopedRules = configuration.sectionAllocationRules.filter((rule) => rule.examName !== null);
    const legacyRules = configuration.sectionAllocationRules.filter((rule) => rule.examName === null);

    if (legacyRules.length === 0) {
        return cloneValue(configuration);
    }

    const sectionAllocationRules = examNameOptions.flatMap(({ value: examName }) => {
        const existingRules = scopedRules.filter((rule) => rule.examName === examName);
        if (existingRules.length > 0) {
            return existingRules;
        }

        const availableTaskTypes = new Set(
            getExamTaskTypesByExamName(examName).map((taskType) => taskType.apiType),
        );

        return legacyRules
            .filter((rule) => availableTaskTypes.has(rule.examTaskType))
            .map((rule) => ({ ...rule, examName }));
    });

    return {
        ...cloneValue(configuration),
        sectionAllocationRules,
    };
}

function createDefaultTaskDraft(examName: ExamName, taskType: ExamTaskTypeOption | null): TaskMarkingConfig {
    if (!taskType) {
        return createDefaultTaskMarkingConfig(examName, '', 'Objective');
    }

    const draft = createDefaultTaskMarkingConfig(
        examName,
        taskType.apiType,
        getDefaultRuleKindForCategory(taskType.category),
    );
    if (taskType.category === 'Speaking') {
        draft.speechAnalysis = createDefaultSpeechAnalysisRubricConfig();
    }
    return draft;
}

function getTaskTypeLabel(option: ExamTaskTypeOption, examName: ExamName): string {
    return getExamTaskTypeLabel(option, examName) ?? option.label;
}

function setTaskTypeIfMissing(
    examName: ExamName,
    taskConfigs: AdminTaskMarkingConfigDTO[],
    currentTaskType: string,
): string {
    const taskTypesForExam = getExamTaskTypesByExamName(examName);
    if (taskTypesForExam.length === 0) return '';

    if (currentTaskType && taskTypesForExam.some((item) => item.apiType === currentTaskType)) {
        return currentTaskType;
    }

    const configuredTaskType = taskTypesForExam.find((item) =>
        taskConfigs.some((config) => config.examName === examName && config.examTaskType === item.apiType),
    );

    return (configuredTaskType ?? taskTypesForExam[0]).apiType;
}

function getTaskDraftBaseline(
    examName: ExamName,
    selectedTaskType: string,
    taskConfigMap: Map<string, AdminTaskMarkingConfigDTO>,
): TaskMarkingConfig {
    const selectedType = examTaskTypes.find((item) => item.apiType === selectedTaskType) ?? null;
    const existingConfig = taskConfigMap.get(taskKey(examName, selectedTaskType)) ?? null;

    if (existingConfig) {
        const draft = cloneValue(existingConfig.configuration);
        if (selectedType?.category === 'Speaking' && !draft.speechAnalysis) {
            draft.speechAnalysis = createDefaultSpeechAnalysisRubricConfig();
        }
        return draft;
    }

    return createDefaultTaskDraft(examName, selectedType);
}

function getMockTestBaseline(mockTestConfig: AdminMockTestScoringConfigDTO | null): MockTestScoringConfiguration {
    return mockTestConfig ? scopeLegacyMockTestRules(mockTestConfig.configuration) : createDefaultMockTestDraft();
}

function isComponentConfigValid(component: MarkingComponentConfig): boolean {
    if (component.outOf < 0) {
        return false;
    }

    switch (component.ruleKind) {
        case 'AbsoluteBands':
        case 'ErrorCountBands':
            return component.thresholdBands.length > 0;
        case 'RelativeBands':
            return component.relativeThresholdBands.length > 0;
        case 'WordCountBands':
            return component.wordCountBands.length > 0;
        case 'AppGrammarMetric':
            return component.appGrammarMetric !== null;
        default:
            return true;
    }
}

function isTaskConfigValid(taskDraft: TaskMarkingConfig): boolean {
    if (taskDraft.speechAnalysis && !isSpeechAnalysisRubricValid(taskDraft.speechAnalysis)) {
        return false;
    }

    if (taskDraft.ruleKind === 'Objective') {
        return taskDraft.objective !== null;
    }

    return taskDraft.components.length > 0 && taskDraft.components.every(isComponentConfigValid);
}

function isSpeechAnalysisRubricValid(rubric: SpeechAnalysisRubricConfig): boolean {
    const { fluency, pronunciation } = rubric;
    const punctuationPauseValid =
        fluency.punctuationPauseMaxSeconds === null ||
        (Number.isFinite(fluency.punctuationPauseMaxSeconds) &&
            fluency.punctuationPauseMaxSeconds >= 0);

    return (
        rubric.rubricVersion.trim().length > 0 &&
        rubric.rubricVersion.trim().length <= 32 &&
        Number.isFinite(fluency.pauseDisplayMinSeconds) &&
        fluency.pauseDisplayMinSeconds >= 0 &&
        Number.isInteger(fluency.freeBriefPauseCount) &&
        fluency.freeBriefPauseCount >= 0 &&
        Number.isFinite(fluency.briefPauseMaxSeconds) &&
        fluency.briefPauseMaxSeconds >= 0 &&
        Number.isFinite(fluency.longPauseMinSeconds) &&
        fluency.longPauseMinSeconds >= 0 &&
        fluency.briefPauseMaxSeconds < fluency.longPauseMinSeconds &&
        Number.isInteger(fluency.freeLongPauseCount) &&
        fluency.freeLongPauseCount >= 0 &&
        Number.isFinite(fluency.longPauseMaxSeconds) &&
        fluency.longPauseMaxSeconds >= fluency.longPauseMinSeconds &&
        Number.isFinite(fluency.longPauseCountPenalty) &&
        Number.isFinite(fluency.veryLongPauseCountPenalty) &&
        punctuationPauseValid &&
        Number.isFinite(pronunciation.minWhisperWordScore) &&
        pronunciation.minWhisperWordScore >= 0 &&
        pronunciation.minWhisperWordScore <= 1 &&
        Number.isInteger(pronunciation.minimumReliableWords) &&
        pronunciation.minimumReliableWords >= 0
    );
}

function isMockTestConfigValid(mockTestDraft: MockTestScoringConfiguration): boolean {
    if (
        mockTestDraft.normalizedScoreOutOf !== 90 ||
        mockTestDraft.minimumNormalizedScore !== 10 ||
        mockTestDraft.maximumNormalizedScore !== 90 ||
        mockTestDraft.sectionAllocationRules.length === 0 ||
        mockTestDraft.sectionAllocationRules.some(
            (rule) => rule.examName === null || !Number.isFinite(rule.allocatedMark) || rule.allocatedMark <= 0,
        )
    ) {
        return false;
    }

    if (examNameOptions.some(({ value }) => !mockTestDraft.sectionAllocationRules.some((rule) => rule.examName === value))) {
        return false;
    }

    if (
        examNameOptions.some(({ value: examName }) =>
            examTaskCategories.some((category) => {
                const sectionRules = mockTestDraft.sectionAllocationRules.filter(
                    (rule) => rule.examName === examName && rule.sectionCategory === category.id,
                );
                const sectionTotal = sectionRules.reduce((sum, rule) => sum + rule.allocatedMark, 0);

                return (
                    sectionRules.length === 0 ||
                    Math.abs(sectionTotal - mockTestSectionAllocationTarget) >= mockTestSectionAllocationTolerance
                );
            }),
        )
    ) {
        return false;
    }

    const uniqueRuleKeys = new Set(
        mockTestDraft.sectionAllocationRules.map(
            (rule) => `${rule.examName}:${rule.sectionCategory}:${rule.examTaskType}`,
        ),
    );

    return uniqueRuleKeys.size === mockTestDraft.sectionAllocationRules.length;
}

function SummaryCard({ label, value, helper }: SummaryCardProps) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                borderRadius: 3,
                minWidth: 0,
                borderColor: 'rgba(148, 163, 184, 0.24)',
                background: 'linear-gradient(180deg, rgba(248,250,252,0.98) 0%, rgba(241,245,249,0.96) 100%)',
                boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)',
            }}
        >
            <Stack spacing={0.5}>
                <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: '0.12em' }}>
                    {label}
                </Typography>
                <Typography variant="h5" sx={{ color: '#0f172a', fontWeight: 800 }}>
                    {value}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                    {helper}
                </Typography>
            </Stack>
        </Paper>
    );
}

type SelectFieldProps = {
    label: string;
    value: string;
    options: OptionItem[];
    onChange: (value: string) => void;
    helperText?: string;
    fullWidth?: boolean;
};

function SelectField({ label, value, options, onChange, helperText, fullWidth = true }: SelectFieldProps) {
    return (
        <TextField
            select
            fullWidth={fullWidth}
            label={label}
            value={value}
            onChange={(event) => onChange(String(event.target.value))}
            helperText={helperText}
        >
            {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </TextField>
    );
}

type NumberFieldProps = {
    label: string;
    value: number | null;
    onChange: (value: number | null) => void;
    helperText?: string;
    nullable?: boolean;
    step?: string;
    min?: number;
    max?: number;
    disabled?: boolean;
};

function NumberField({
    label,
    value,
    onChange,
    helperText,
    nullable = false,
    step = 'any',
    min,
    max,
    disabled = false,
}: NumberFieldProps) {
    return (
        <TextField
            fullWidth
            type="number"
            label={label}
            value={value ?? ''}
            helperText={helperText}
            disabled={disabled}
            onChange={(event) => {
                const rawValue = event.target.value;
                if (!rawValue.trim()) {
                    onChange(nullable ? null : 0);
                    return;
                }

                const nextValue = Number(rawValue);
                onChange(Number.isFinite(nextValue) ? nextValue : (nullable ? null : 0));
            }}
            inputProps={{ step, min, max }}
        />
    );
}

function TaskConfigPageSectionHeader({
    examName,
    taskType,
    selected,
    existingConfig,
}: {
    examName: ExamName;
    taskType: ExamTaskTypeOption | null;
    selected: boolean;
    existingConfig: AdminTaskMarkingConfigDTO | null;
}) {
    return (
        <Stack spacing={0.75}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                    {taskType ? getTaskTypeLabel(taskType, examName) : 'Select a task type'}
                </Typography>
                <Chip size="small" label={selected ? 'Selected' : 'Preview'} color={selected ? 'primary' : 'default'} />
                <Chip
                    size="small"
                    label={existingConfig ? 'Configured' : 'New'}
                    color={existingConfig ? 'success' : 'warning'}
                    variant="outlined"
                />
                {taskType ? <Chip size="small" label={taskType.category} variant="outlined" /> : null}
            </Stack>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
                {existingConfig
                    ? `Updated ${formatDateTime(existingConfig.updatedAt)}`
                    : 'This task type does not have a saved config yet. Create one using the draft below.'}
            </Typography>
        </Stack>
    );
}

type TaskConfigTabProps = {
    examName: ExamName;
    onExamNameChange: (examName: ExamName) => void;
    taskTypes: ExamTaskTypeOption[];
    selectedTaskType: string;
    onSelectTaskType: (taskType: string) => void;
    taskConfigs: AdminTaskMarkingConfigDTO[];
    taskDraft: TaskMarkingConfig;
    onTaskDraftChange: (draft: TaskMarkingConfig) => void;
    onTaskSave: () => Promise<void>;
    onTaskReset: () => void;
    isSavingTask: boolean;
    isTaskDirty: boolean;
    taskSearch: string;
    onTaskSearchChange: (value: string) => void;
};

function TaskConfigTab({
    examName,
    onExamNameChange,
    taskTypes,
    selectedTaskType,
    onSelectTaskType,
    taskConfigs,
    taskDraft,
    onTaskDraftChange,
    onTaskSave,
    onTaskReset,
    isSavingTask,
    isTaskDirty,
    taskSearch,
    onTaskSearchChange,
}: TaskConfigTabProps) {
    const taskConfigMap = useMemo(() => {
        const map = new Map<string, AdminTaskMarkingConfigDTO>();
        for (const config of taskConfigs) {
            map.set(taskKey(config.examName, config.examTaskType), config);
        }
        return map;
    }, [taskConfigs]);

    const filteredTaskTypes = useMemo(() => {
        const normalizedSearch = taskSearch.trim().toLowerCase();
        if (!normalizedSearch) return taskTypes;

        return taskTypes.filter((taskType) => {
            const label = getTaskTypeLabel(taskType, examName).toLowerCase();
            const apiType = taskType.apiType.toLowerCase();
            const category = taskType.category.toLowerCase();
            return label.includes(normalizedSearch) || apiType.includes(normalizedSearch) || category.includes(normalizedSearch);
        });
    }, [examName, taskSearch, taskTypes]);

    const groupedTaskTypes = useMemo(() => {
        const filteredKeys = new Set(filteredTaskTypes.map((taskType) => taskType.apiType));
        return examTaskCategories.map((category) => ({
            category,
            taskTypes: taskTypes.filter((taskType) => taskType.category === category.id && filteredKeys.has(taskType.apiType)),
        }));
    }, [filteredTaskTypes, taskTypes]);

    const configuredCount = useMemo(
        () => taskTypes.filter((taskType) => taskConfigMap.has(taskKey(examName, taskType.apiType))).length,
        [examName, taskConfigMap, taskTypes],
    );

    const selectedTaskTypeOption = taskTypes.find((taskType) => taskType.apiType === selectedTaskType) ?? null;
    const selectedConfig = selectedTaskType ? taskConfigMap.get(taskKey(examName, selectedTaskType)) ?? null : null;

    return (
        <Box
            sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: {
                    xs: '1fr',
                    xl: '360px minmax(0, 1fr)',
                },
            }}
        >
            <Paper
                variant="outlined"
                sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    borderColor: 'rgba(148, 163, 184, 0.24)',
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)',
                }}
            >
                <Stack spacing={2} sx={{ p: 2.5 }}>
                    <Stack spacing={1}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                            Exam Scope
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                            Select the exam name first, then choose the task type you want to configure.
                        </Typography>
                    </Stack>
                    <SegmentedTabs
                        label="Exam Name"
                        value={examName}
                        options={examNameOptions.map((option) => ({ value: option.value, label: option.label }))}
                        onChange={(value) => onExamNameChange(value as ExamName)}
                        helperText={`${configuredCount} of ${taskTypes.length} task types configured for ${examName}`}
                    />
                    <TextField
                        label="Search task types"
                        value={taskSearch}
                        onChange={(event) => onTaskSearchChange(event.target.value)}
                        placeholder="Filter by label, API type, or category"
                        fullWidth
                    />
                </Stack>
                <Divider />
                <List disablePadding sx={{ maxHeight: 780, overflowY: 'auto' }}>
                    {groupedTaskTypes.map(({ category, taskTypes: categoryTaskTypes }) => (
                        <Box key={category.id} sx={{ py: 1 }}>
                            <Box sx={{ px: 2.5, pb: 0.75 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        color: '#64748b',
                                        fontWeight: 800,
                                        letterSpacing: '0.14em',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {category.label}
                                </Typography>
                            </Box>
                            {categoryTaskTypes.length === 0 ? (
                                <Box sx={{ px: 2.5, pb: 1.5 }}>
                                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                                        No task types match the current search.
                                    </Typography>
                                </Box>
                            ) : (
                                categoryTaskTypes.map((taskType) => {
                                    const config = taskConfigMap.get(taskKey(examName, taskType.apiType)) ?? null;
                                    const isSelected = taskType.apiType === selectedTaskType;
                                    return (
                                        <ListItemButton
                                            key={taskType.apiType}
                                            selected={isSelected}
                                            onClick={() => onSelectTaskType(taskType.apiType)}
                                            sx={{
                                                mx: 1.5,
                                                mb: 0.75,
                                                borderRadius: 2.5,
                                                alignItems: 'stretch',
                                                border: '1px solid transparent',
                                                backgroundColor: isSelected ? 'rgba(59, 130, 246, 0.08)' : 'transparent',
                                                borderColor: isSelected ? 'rgba(59, 130, 246, 0.22)' : 'transparent',
                                            }}
                                        >
                                            <Stack spacing={0.75} sx={{ width: '100%' }}>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="flex-start"
                                                    justifyContent="space-between"
                                                    useFlexGap
                                                    flexWrap="wrap"
                                                >
                                                    <Box sx={{ minWidth: 0, flex: '1 1 0' }}>
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{
                                                                color: '#0f172a',
                                                                fontWeight: 800,
                                                                lineHeight: 1.25,
                                                            }}
                                                        >
                                                            {getTaskTypeLabel(taskType, examName)}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                            {taskType.apiType}
                                                        </Typography>
                                                    </Box>
                                                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                                        <Chip
                                                            size="small"
                                                            label={config ? 'Configured' : 'New'}
                                                            color={config ? 'success' : 'warning'}
                                                            variant="outlined"
                                                        />
                                                        <Chip size="small" label={taskType.category} variant="outlined" />
                                                    </Stack>
                                                </Stack>
                                                <Typography variant="body2" sx={{ color: '#475569' }}>
                                                    {config
                                                        ? `Updated ${formatDateTime(config.updatedAt)}`
                                                        : `Create a new ${taskType.category.toLowerCase()} config from scratch.`}
                                                </Typography>
                                            </Stack>
                                        </ListItemButton>
                                    );
                                })
                            )}
                        </Box>
                    ))}
                </List>
            </Paper>

            <TaskConfigEditor
                examName={examName}
                selectedTaskType={selectedTaskTypeOption}
                selectedConfig={selectedConfig}
                taskDraft={taskDraft}
                onTaskDraftChange={onTaskDraftChange}
                onTaskSave={onTaskSave}
                onTaskReset={onTaskReset}
                isSavingTask={isSavingTask}
                isTaskDirty={isTaskDirty}
            />
        </Box>
    );
}

function voiceTaskType(examTaskType: string): string {
    return examTaskType
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .replace(/[_-]+/g, '_')
        .toLowerCase();
}

function SpeechAnalysisRubricEditor({
    examName,
    examTaskType,
    rubric,
    onChange,
}: {
    examName: ExamName;
    examTaskType: string;
    rubric: SpeechAnalysisRubricConfig;
    onChange: (rubric: SpeechAnalysisRubricConfig) => void;
}) {
    const taskType = voiceTaskType(examTaskType);
    const rubricId = `${examName.toLowerCase()}_${taskType}`;
    const updateFluency = (
        field: keyof SpeechAnalysisRubricConfig['fluency'],
        value: number | null,
    ) => {
        onChange({
            ...rubric,
            fluency: { ...rubric.fluency, [field]: value },
        });
    };
    const updatePronunciation = (
        field: keyof SpeechAnalysisRubricConfig['pronunciation'],
        value: number | boolean,
    ) => {
        onChange({
            ...rubric,
            pronunciation: { ...rubric.pronunciation, [field]: value },
        });
    };

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2.5,
                borderRadius: 3,
                borderColor: 'rgba(192, 0, 99, 0.24)',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(253,242,248,0.48) 100%)',
            }}
        >
            <Stack spacing={2.5}>
                <Stack spacing={0.5}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                        Speech Analysis Rubric
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                        The API loads this saved rubric for the selected exam and speaking task, adds the task reference text,
                        and forwards both with the learner audio to Voice Analysis V2.
                    </Typography>
                </Stack>

                <Alert severity="info">
                    The learner app sends audio and task identity only. Rubric ID, task type, reference text, and these values
                    are resolved by the API. Rubric ID and task type cannot be edited here.
                </Alert>

                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                    }}
                >
                    <TextField label="Rubric ID" value={rubricId} disabled fullWidth />
                    <TextField label="Voice task type" value={taskType} disabled fullWidth />
                    <TextField
                        label="Rubric version"
                        value={rubric.rubricVersion}
                        inputProps={{ maxLength: 32 }}
                        onChange={(event) => onChange({ ...rubric, rubricVersion: event.target.value })}
                        helperText="Change this whenever the scoring policy changes"
                        fullWidth
                    />
                </Box>

                <Divider />

                <Stack spacing={1.5}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a' }}>
                            Fluency pause policy
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                            Configure how many pauses are free, which gaps count, and the penalty for additional long pauses.
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
                        }}
                    >
                        <NumberField
                            label="Pause display minimum (seconds)"
                            value={rubric.fluency.pauseDisplayMinSeconds}
                            min={0}
                            step="0.01"
                            helperText="Every gap at or above this value is shown in Fluency"
                            onChange={(value) => updateFluency('pauseDisplayMinSeconds', value ?? 0.25)}
                        />
                        <NumberField
                            label="Free brief pause count"
                            value={rubric.fluency.freeBriefPauseCount}
                            min={0}
                            step="1"
                            onChange={(value) => updateFluency('freeBriefPauseCount', value ?? 0)}
                        />
                        <NumberField
                            label="Brief pause maximum (seconds)"
                            value={rubric.fluency.briefPauseMaxSeconds}
                            min={0}
                            step="0.01"
                            onChange={(value) => updateFluency('briefPauseMaxSeconds', value ?? 0)}
                        />
                        <NumberField
                            label="Long pause minimum (seconds)"
                            value={rubric.fluency.longPauseMinSeconds}
                            min={0}
                            step="0.01"
                            onChange={(value) => updateFluency('longPauseMinSeconds', value ?? 0)}
                        />
                        <NumberField
                            label="Free long pause count"
                            value={rubric.fluency.freeLongPauseCount}
                            min={0}
                            step="1"
                            onChange={(value) => updateFluency('freeLongPauseCount', value ?? 0)}
                        />
                        <NumberField
                            label="Long pause maximum (seconds)"
                            value={rubric.fluency.longPauseMaxSeconds}
                            min={0}
                            step="0.01"
                            onChange={(value) => updateFluency('longPauseMaxSeconds', value ?? 0)}
                        />
                        <NumberField
                            label="Penalty per extra long pause (within maximum)"
                            value={rubric.fluency.longPauseCountPenalty}
                            step="0.1"
                            helperText="Applied after the free long-pause count, from the long minimum through the long maximum"
                            onChange={(value) => updateFluency('longPauseCountPenalty', value ?? 0)}
                        />
                        <NumberField
                            label="Penalty per very long pause (over maximum)"
                            value={rubric.fluency.veryLongPauseCountPenalty}
                            step="0.1"
                            helperText="Applied to every pause above the long-pause maximum; no free allowance"
                            onChange={(value) => updateFluency('veryLongPauseCountPenalty', value ?? 0)}
                        />
                        <NumberField
                            label="Punctuation pause maximum (seconds)"
                            value={rubric.fluency.punctuationPauseMaxSeconds}
                            nullable
                            min={0}
                            step="0.01"
                            helperText="Leave empty to disable punctuation-pause exemption"
                            onChange={(value) => updateFluency('punctuationPauseMaxSeconds', value)}
                        />
                    </Box>
                </Stack>

                <Divider />

                <Stack spacing={1.5}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a' }}>
                            Pronunciation evidence
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                            Control which recognised words are reliable enough to contribute pronunciation evidence.
                        </Typography>
                    </Box>
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={rubric.pronunciation.excludeLowConfidenceWords}
                                onChange={(event) => updatePronunciation('excludeLowConfidenceWords', event.target.checked)}
                            />
                        )}
                        label="Exclude low-confidence recognised words"
                    />
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                        }}
                    >
                        <NumberField
                            label="Minimum Whisper word confidence"
                            value={rubric.pronunciation.minWhisperWordScore}
                            min={0}
                            max={1}
                            step="0.01"
                            onChange={(value) => updatePronunciation('minWhisperWordScore', value ?? 0)}
                        />
                        <NumberField
                            label="Minimum reliable words"
                            value={rubric.pronunciation.minimumReliableWords}
                            min={0}
                            step="1"
                            onChange={(value) => updatePronunciation('minimumReliableWords', value ?? 3)}
                        />
                    </Box>
                </Stack>

                {!isSpeechAnalysisRubricValid(rubric) ? (
                    <Alert severity="error">
                        Enter finite, non-negative durations and counts. Brief pause maximum must stay below the long pause
                        minimum, and long pause maximum must not be lower than the long pause minimum.
                    </Alert>
                ) : null}
            </Stack>
        </Paper>
    );
}

function TaskConfigEditor({
    examName,
    selectedTaskType,
    selectedConfig,
    taskDraft,
    onTaskDraftChange,
    onTaskSave,
    onTaskReset,
    isSavingTask,
    isTaskDirty,
}: {
    examName: ExamName;
    selectedTaskType: ExamTaskTypeOption | null;
    selectedConfig: AdminTaskMarkingConfigDTO | null;
    taskDraft: TaskMarkingConfig;
    onTaskDraftChange: (draft: TaskMarkingConfig) => void;
    onTaskSave: () => Promise<void>;
    onTaskReset: () => void;
    isSavingTask: boolean;
    isTaskDirty: boolean;
}) {
    const handleUpdateObjective = (field: keyof ObjectiveMarkingConfig, value: number) => {
        const objective = taskDraft.objective ?? createDefaultObjectiveMarkingConfig();
        onTaskDraftChange({
            ...taskDraft,
            objective: {
                ...objective,
                [field]: value,
            },
        });
    };

    const handleRuleKindChange = (ruleKind: MarkingTaskRuleKind) => {
        if (ruleKind === taskDraft.ruleKind) return;

        if (ruleKind === 'Objective') {
            onTaskDraftChange({
                ...taskDraft,
                ruleKind,
                objective: taskDraft.objective ?? createDefaultObjectiveMarkingConfig(),
                components: taskDraft.components,
            });
            return;
        }

        onTaskDraftChange({
            ...taskDraft,
            ruleKind,
            components: taskDraft.components.length > 0 ? taskDraft.components : [createDefaultMarkingComponentConfig()],
        });
    };

    const handleUpdateComponent = (index: number, nextComponent: MarkingComponentConfig) => {
        const nextComponents = taskDraft.components.map((component, componentIndex) =>
            componentIndex === index ? nextComponent : component,
        );
        onTaskDraftChange({ ...taskDraft, components: nextComponents });
    };

    const handleAddComponent = () => {
        onTaskDraftChange({
            ...taskDraft,
            components: [...taskDraft.components, createDefaultMarkingComponentConfig()],
        });
    };

    const handleRemoveComponent = (index: number) => {
        const nextComponents = taskDraft.components.filter((_, componentIndex) => componentIndex !== index);
        onTaskDraftChange({
            ...taskDraft,
            components: nextComponents.length > 0 ? nextComponents : [createDefaultMarkingComponentConfig()],
        });
    };

    return (
        <Card
            sx={{
                borderRadius: 3,
                border: '1px solid rgba(148, 163, 184, 0.2)',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(248,250,252,0.99) 100%)',
                boxShadow: '0 18px 46px rgba(15, 23, 42, 0.10)',
            }}
        >
            <Title title="Marking Configuration" />
            <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                <Stack spacing={3}>
                    <Stack
                        direction={{ xs: 'column', lg: 'row' }}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems={{ lg: 'flex-start' }}
                    >
                        <TaskConfigPageSectionHeader
                            examName={examName}
                            taskType={selectedTaskType}
                            selected
                            existingConfig={selectedConfig}
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <Button variant="outlined" onClick={onTaskReset} disabled={!isTaskDirty || isSavingTask}>
                                Reset
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => void onTaskSave()}
                                disabled={isSavingTask || !isTaskDirty || !isTaskConfigValid(taskDraft)}
                            >
                                {selectedConfig ? 'Save Configuration' : 'Create Configuration'}
                            </Button>
                        </Stack>
                    </Stack>

                    <Alert severity="info">
                        Objective scoring is used by the reading and listening tasks that rely on answer accuracy.
                        Component-based scoring powers the speaking and writing rules with component-specific marks.
                    </Alert>

                    {selectedTaskType?.category === 'Speaking' && taskDraft.speechAnalysis ? (
                        <SpeechAnalysisRubricEditor
                            examName={examName}
                            examTaskType={selectedTaskType.apiType}
                            rubric={taskDraft.speechAnalysis}
                            onChange={(speechAnalysis) => onTaskDraftChange({ ...taskDraft, speechAnalysis })}
                        />
                    ) : null}

                    <Stack spacing={2}>
                        <SelectField
                            label="Rule Kind"
                            value={taskDraft.ruleKind}
                            options={markingTaskRuleKindOptions}
                            onChange={(value) => handleRuleKindChange(value as MarkingTaskRuleKind)}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={taskDraft.includeTotalRow}
                                    onChange={(event) =>
                                        onTaskDraftChange({
                                            ...taskDraft,
                                            includeTotalRow: event.target.checked,
                                        })
                                    }
                                />
                            }
                            label="Include total row"
                        />
                    </Stack>

                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2.5,
                            borderRadius: 3,
                            borderColor: 'rgba(148, 163, 184, 0.2)',
                            background: 'linear-gradient(180deg, rgba(248,250,252,0.98) 0%, rgba(241,245,249,0.98) 100%)',
                        }}
                    >
                        <Stack spacing={2}>
                            <Stack spacing={0.5}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                    Objective Settings
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                    These values control the accuracy-based mark model used by objective-style tasks.
                                </Typography>
                            </Stack>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        md: 'repeat(2, minmax(0, 1fr))',
                                    },
                                }}
                            >
                                <NumberField
                                    label="Correct answer score"
                                    value={taskDraft.objective?.correctAnswerScore ?? 0}
                                    onChange={(value) => handleUpdateObjective('correctAnswerScore', value ?? 0)}
                                />
                                <NumberField
                                    label="Wrong answer penalty"
                                    value={taskDraft.objective?.wrongAnswerPenalty ?? 0}
                                    onChange={(value) => handleUpdateObjective('wrongAnswerPenalty', value ?? 0)}
                                />
                                <NumberField
                                    label="Minimum score"
                                    value={taskDraft.objective?.minimumScore ?? 0}
                                    onChange={(value) => handleUpdateObjective('minimumScore', value ?? 0)}
                                />
                                <NumberField
                                    label="Single choice correct score"
                                    value={taskDraft.objective?.singleChoiceCorrectScore ?? 0}
                                    onChange={(value) => handleUpdateObjective('singleChoiceCorrectScore', value ?? 0)}
                                />
                                <NumberField
                                    label="Reorder adjacent pair score"
                                    value={taskDraft.objective?.reorderAdjacentPairScore ?? 0}
                                    onChange={(value) => handleUpdateObjective('reorderAdjacentPairScore', value ?? 0)}
                                />
                            </Box>
                        </Stack>
                    </Paper>

                    {taskDraft.ruleKind === 'ComponentBased' ? (
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2.5,
                                borderRadius: 3,
                                borderColor: 'rgba(148, 163, 184, 0.2)',
                                background: 'linear-gradient(180deg, rgba(248,250,252,0.98) 0%, rgba(241,245,249,0.98) 100%)',
                            }}
                        >
                            <Stack spacing={2.5}>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    alignItems={{ sm: 'center' }}
                                    justifyContent="space-between"
                                    spacing={1}
                                >
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                            Component Rules
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                                            Add one row per score dimension used by the backend component-based scorer.
                                        </Typography>
                                    </Box>
                                    <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={handleAddComponent}>
                                        Add Component
                                    </Button>
                                </Stack>
                                <Stack spacing={2}>
                                    {taskDraft.components.map((component, index) => (
                                        <TaskComponentEditor
                                            key={`${component.title}-${index}`}
                                            component={component}
                                            onChange={(nextComponent) => handleUpdateComponent(index, nextComponent)}
                                            onRemove={() => handleRemoveComponent(index)}
                                            canRemove={taskDraft.components.length > 1}
                                        />
                                    ))}
                                </Stack>
                            </Stack>
                        </Paper>
                    ) : null}

                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button variant="outlined" onClick={onTaskReset} disabled={!isTaskDirty || isSavingTask}>
                            Reset
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => void onTaskSave()}
                            disabled={isSavingTask || !isTaskDirty || !isTaskConfigValid(taskDraft)}
                        >
                            {selectedConfig ? 'Save Configuration' : 'Create Configuration'}
                        </Button>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}

function TaskComponentEditor({
    component,
    onChange,
    onRemove,
    canRemove,
}: {
    component: MarkingComponentConfig;
    onChange: (component: MarkingComponentConfig) => void;
    onRemove: () => void;
    canRemove: boolean;
}) {
    const handleRuleKindChange = (ruleKind: MarkingComponentRuleKind) => {
        const nextComponent: MarkingComponentConfig = {
            ...component,
            ruleKind,
            appGrammarMetric: ruleKind === 'AppGrammarMetric' ? component.appGrammarMetric ?? 'Grammar' : component.appGrammarMetric,
            thresholdBands:
                ruleKind === 'AbsoluteBands' || ruleKind === 'ErrorCountBands'
                    ? component.thresholdBands.length > 0
                        ? component.thresholdBands
                        : [createDefaultThresholdBand()]
                    : component.thresholdBands,
            wordCountBands:
                ruleKind === 'WordCountBands'
                    ? component.wordCountBands.length > 0
                        ? component.wordCountBands
                        : [createDefaultWordCountBand()]
                    : component.wordCountBands,
            relativeThresholdBands:
                ruleKind === 'RelativeBands'
                    ? component.relativeThresholdBands.length > 0
                        ? component.relativeThresholdBands
                        : [createDefaultRelativeThresholdBand()]
                    : component.relativeThresholdBands,
        };

        if (ruleKind !== 'AppGrammarMetric') {
            nextComponent.appGrammarMetric = null;
        }

        onChange(nextComponent);
    };

    const updateThresholdBands = (nextBands: ThresholdScoreBand[]) => {
        onChange({ ...component, thresholdBands: nextBands });
    };

    const updateWordCountBands = (nextBands: WordCountScoreBand[]) => {
        onChange({ ...component, wordCountBands: nextBands });
    };

    const updateRelativeThresholdBands = (nextBands: RelativeThresholdScoreBand[]) => {
        onChange({ ...component, relativeThresholdBands: nextBands });
    };

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2.25,
                borderRadius: 3,
                borderColor: 'rgba(148, 163, 184, 0.22)',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.96) 100%)',
            }}
        >
            <Stack spacing={2}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ sm: 'center' }}
                    justifyContent="space-between"
                >
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a' }}>
                            Component: {component.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                            Configure the backend component title, rule kind, and any band tables or grammar metrics.
                        </Typography>
                    </Box>
                    <IconButton onClick={onRemove} disabled={!canRemove} aria-label="Remove component">
                        <DeleteOutlineRoundedIcon />
                    </IconButton>
                </Stack>

                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                        gridTemplateColumns: {
                            xs: '1fr',
                            md: 'repeat(2, minmax(0, 1fr))',
                        },
                    }}
                >
                    <SelectField
                        label="Title"
                        value={component.title}
                        options={markTitleOptions}
                        onChange={(value) => onChange({ ...component, title: value as MarkTitleValue })}
                    />
                    <SelectField
                        label="Rule Kind"
                        value={component.ruleKind}
                        options={markingComponentRuleKindOptions}
                        onChange={(value) => handleRuleKindChange(value as MarkingComponentRuleKind)}
                    />
                    <NumberField
                        label="Out of"
                        value={component.outOf}
                        onChange={(value) => onChange({ ...component, outOf: value ?? 0 })}
                    />
                    <NumberField
                        label="Fixed score"
                        value={component.fixedScore}
                        onChange={(value) => onChange({ ...component, fixedScore: value ?? 0 })}
                    />
                    <NumberField
                        label="Penalty per error"
                        value={component.penaltyPerError}
                        onChange={(value) => onChange({ ...component, penaltyPerError: value ?? 0 })}
                    />
                    <NumberField
                        label="Round decimals"
                        value={component.roundDecimals}
                        onChange={(value) => onChange({ ...component, roundDecimals: value ?? 0 })}
                        step="1"
                    />
                </Box>

                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={component.requiresPositiveContentScore}
                                onChange={(event) =>
                                    onChange({ ...component, requiresPositiveContentScore: event.target.checked })
                                }
                            />
                        }
                        label="Requires positive content score"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={component.requiresPositiveFormScore}
                                onChange={(event) => onChange({ ...component, requiresPositiveFormScore: event.target.checked })}
                            />
                        }
                        label="Requires positive form score"
                    />
                </Stack>

                {component.ruleKind === 'AppGrammarMetric' ? (
                    <SelectField
                        label="App grammar metric"
                        value={component.appGrammarMetric ?? ''}
                        options={[{ value: '', label: 'Select a metric' }, ...appGrammarMetricKindOptions]}
                        onChange={(value) => onChange({ ...component, appGrammarMetric: (value || null) as AppGrammarMetricKind | null })}
                    />
                ) : null}

                {component.ruleKind === 'AbsoluteBands' || component.ruleKind === 'ErrorCountBands' ? (
                    <ThresholdBandListEditor bands={component.thresholdBands} onChange={updateThresholdBands} />
                ) : null}

                {component.ruleKind === 'WordCountBands' ? (
                    <WordCountBandListEditor bands={component.wordCountBands} onChange={updateWordCountBands} />
                ) : null}

                {component.ruleKind === 'RelativeBands' ? (
                    <RelativeThresholdBandListEditor bands={component.relativeThresholdBands} onChange={updateRelativeThresholdBands} />
                ) : null}
            </Stack>
        </Paper>
    );
}

function ThresholdBandListEditor({
    bands,
    onChange,
}: {
    bands: ThresholdScoreBand[];
    onChange: (bands: ThresholdScoreBand[]) => void;
}) {
    const updateBand = (index: number, nextBand: ThresholdScoreBand) => {
        onChange(bands.map((band, bandIndex) => (bandIndex === index ? nextBand : band)));
    };

    const addBand = () => {
        onChange([...bands, createDefaultThresholdBand()]);
    };

    const removeBand = (index: number) => {
        const nextBands = bands.filter((_, bandIndex) => bandIndex !== index);
        onChange(nextBands.length > 0 ? nextBands : [createDefaultThresholdBand()]);
    };

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a' }}>
                        Threshold bands
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Used by absolute and error-count rule kinds.
                    </Typography>
                </Box>
                <Button size="small" variant="outlined" startIcon={<AddRoundedIcon />} onClick={addBand}>
                    Add band
                </Button>
            </Stack>
            <Stack spacing={1.5}>
                {bands.map((band, index) => (
                    <Paper key={`${index}-${band.score}`} variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                        <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                    Band {index + 1}
                                </Typography>
                                <IconButton onClick={() => removeBand(index)} aria-label={`Remove threshold band ${index + 1}`}>
                                    <DeleteOutlineRoundedIcon />
                                </IconButton>
                            </Stack>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        md: 'repeat(3, minmax(0, 1fr))',
                                    },
                                }}
                            >
                                <NumberField
                                    label="Min inclusive"
                                    value={band.minInclusive}
                                    nullable
                                    onChange={(value) => updateBand(index, { ...band, minInclusive: value })}
                                />
                                <NumberField
                                    label="Max inclusive"
                                    value={band.maxInclusive}
                                    nullable
                                    onChange={(value) => updateBand(index, { ...band, maxInclusive: value })}
                                />
                                <NumberField
                                    label="Score"
                                    value={band.score}
                                    onChange={(value) => updateBand(index, { ...band, score: value ?? 0 })}
                                />
                            </Box>
                        </Stack>
                    </Paper>
                ))}
            </Stack>
        </Stack>
    );
}

function WordCountBandListEditor({
    bands,
    onChange,
}: {
    bands: WordCountScoreBand[];
    onChange: (bands: WordCountScoreBand[]) => void;
}) {
    const updateBand = (index: number, nextBand: WordCountScoreBand) => {
        onChange(bands.map((band, bandIndex) => (bandIndex === index ? nextBand : band)));
    };

    const addBand = () => {
        onChange([...bands, createDefaultWordCountBand()]);
    };

    const removeBand = (index: number) => {
        const nextBands = bands.filter((_, bandIndex) => bandIndex !== index);
        onChange(nextBands.length > 0 ? nextBands : [createDefaultWordCountBand()]);
    };

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a' }}>
                        Word count bands
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Used by word-count driven component rules.
                    </Typography>
                </Box>
                <Button size="small" variant="outlined" startIcon={<AddRoundedIcon />} onClick={addBand}>
                    Add band
                </Button>
            </Stack>
            <Stack spacing={1.5}>
                {bands.map((band, index) => (
                    <Paper key={`${index}-${band.score}`} variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                        <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                    Band {index + 1}
                                </Typography>
                                <IconButton onClick={() => removeBand(index)} aria-label={`Remove word count band ${index + 1}`}>
                                    <DeleteOutlineRoundedIcon />
                                </IconButton>
                            </Stack>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        md: 'repeat(3, minmax(0, 1fr))',
                                    },
                                }}
                            >
                                <NumberField
                                    label="Min inclusive"
                                    value={band.minInclusive}
                                    step="1"
                                    onChange={(value) => updateBand(index, { ...band, minInclusive: value ?? 0 })}
                                />
                                <NumberField
                                    label="Max inclusive"
                                    value={band.maxInclusive}
                                    step="1"
                                    onChange={(value) => updateBand(index, { ...band, maxInclusive: value ?? 0 })}
                                />
                                <NumberField
                                    label="Score"
                                    value={band.score}
                                    onChange={(value) => updateBand(index, { ...band, score: value ?? 0 })}
                                />
                            </Box>
                        </Stack>
                    </Paper>
                ))}
            </Stack>
        </Stack>
    );
}

function RelativeThresholdBandListEditor({
    bands,
    onChange,
}: {
    bands: RelativeThresholdScoreBand[];
    onChange: (bands: RelativeThresholdScoreBand[]) => void;
}) {
    const updateBand = (index: number, nextBand: RelativeThresholdScoreBand) => {
        onChange(bands.map((band, bandIndex) => (bandIndex === index ? nextBand : band)));
    };

    const addBand = () => {
        onChange([...bands, createDefaultRelativeThresholdBand()]);
    };

    const removeBand = (index: number) => {
        const nextBands = bands.filter((_, bandIndex) => bandIndex !== index);
        onChange(nextBands.length > 0 ? nextBands : [createDefaultRelativeThresholdBand()]);
    };

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a' }}>
                        Relative threshold bands
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Used by relative bands that compare matched counts to a reference.
                    </Typography>
                </Box>
                <Button size="small" variant="outlined" startIcon={<AddRoundedIcon />} onClick={addBand}>
                    Add band
                </Button>
            </Stack>
            <Stack spacing={1.5}>
                {bands.map((band, index) => (
                    <Paper key={`${index}-${band.score}`} variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                        <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                    Band {index + 1}
                                </Typography>
                                <IconButton
                                    onClick={() => removeBand(index)}
                                    aria-label={`Remove relative threshold band ${index + 1}`}
                                >
                                    <DeleteOutlineRoundedIcon />
                                </IconButton>
                            </Stack>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        md: 'repeat(2, minmax(0, 1fr))',
                                        xl: 'repeat(4, minmax(0, 1fr))',
                                    },
                                }}
                            >
                                <SelectField
                                    label="Threshold kind"
                                    value={band.thresholdKind}
                                    options={relativeThresholdKindOptions}
                                    onChange={(value) => updateBand(index, { ...band, thresholdKind: value as RelativeThresholdKind })}
                                />
                                <NumberField
                                    label="Threshold value"
                                    value={band.thresholdValue}
                                    onChange={(value) => updateBand(index, { ...band, thresholdValue: value ?? 0 })}
                                />
                                <NumberField
                                    label="Offset"
                                    value={band.offset}
                                    onChange={(value) => updateBand(index, { ...band, offset: value ?? 0 })}
                                />
                                <NumberField
                                    label="Divisor"
                                    value={band.divisor}
                                    onChange={(value) => updateBand(index, { ...band, divisor: value ?? 1 })}
                                />
                                <NumberField
                                    label="Score"
                                    value={band.score}
                                    onChange={(value) => updateBand(index, { ...band, score: value ?? 0 })}
                                />
                            </Box>
                        </Stack>
                    </Paper>
                ))}
            </Stack>
        </Stack>
    );
}

function MockTestScoringEditor({
    draft,
    onChange,
    onSave,
    onReset,
    isSaving,
    isDirty,
    config,
}: {
    draft: MockTestScoringConfiguration;
    onChange: (draft: MockTestScoringConfiguration) => void;
    onSave: () => Promise<void>;
    onReset: () => void;
    isSaving: boolean;
    isDirty: boolean;
    config: AdminMockTestScoringConfigDTO | null;
}) {
    const [selectedExamName, setSelectedExamName] = useState<ExamName>('Academic');
    const selectedRules = draft.sectionAllocationRules.filter((rule) => rule.examName === selectedExamName);
    const invalidSectionLabels = examNameOptions.flatMap(({ value: examName, label: examLabel }) =>
        examTaskCategories.flatMap((category) => {
            const sectionRules = draft.sectionAllocationRules.filter(
                (rule) => rule.examName === examName && rule.sectionCategory === category.id,
            );
            const sectionTotal = sectionRules.reduce((sum, rule) => sum + rule.allocatedMark, 0);

            return sectionRules.length > 0 &&
                Math.abs(sectionTotal - mockTestSectionAllocationTarget) < mockTestSectionAllocationTolerance
                ? []
                : [`${examLabel} ${category.label} (${sectionTotal.toFixed(2)})`];
        }),
    );

    const updateRuleWeight = (targetRule: MockTestSectionAllocationConfig, allocatedMark: number) => {
        onChange({
            ...draft,
            sectionAllocationRules: draft.sectionAllocationRules.map((rule) =>
                rule.examName === targetRule.examName &&
                rule.sectionCategory === targetRule.sectionCategory &&
                rule.examTaskType === targetRule.examTaskType
                    ? { ...rule, allocatedMark }
                    : rule,
            ),
        });
    };

    return (
        <Card
            sx={{
                borderRadius: 3,
                border: '1px solid rgba(148, 163, 184, 0.2)',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(248,250,252,0.99) 100%)',
                boxShadow: '0 18px 46px rgba(15, 23, 42, 0.10)',
            }}
        >
            <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                <Stack spacing={3}>
                    <Stack
                        direction={{ xs: 'column', lg: 'row' }}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems={{ lg: 'flex-start' }}
                    >
                        <Stack spacing={0.75}>
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                    Mock Test Scoring
                                </Typography>
                                <Chip size="small" label={config ? 'Configured' : 'New'} color={config ? 'success' : 'warning'} variant="outlined" />
                            </Stack>
                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                                Edit Academic and Core score distributions from the same screen.
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                                {config ? `Updated ${formatDateTime(config.updatedAt)}` : 'No saved mock test scoring config exists yet.'}
                            </Typography>
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <Button variant="outlined" onClick={onReset} disabled={!isDirty || isSaving}>
                                Reset
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => void onSave()}
                                disabled={isSaving || !isDirty || !isMockTestConfigValid(draft)}
                            >
                                {config ? 'Save Scoring Config' : 'Create Scoring Config'}
                            </Button>
                        </Stack>
                    </Stack>

                    <Alert severity="info">
                        Each communicative skill keeps the fixed 10-point base and distributes the remaining 80 points across
                        its question types. The final learner score remains on the 10–90 scale.
                    </Alert>

                    {invalidSectionLabels.length > 0 ? (
                        <Alert severity="error">
                            Save is blocked until every section totals exactly 80.00. Check: {invalidSectionLabels.join(', ')}.
                        </Alert>
                    ) : (
                        <Alert severity="success">All Academic and Core sections total exactly 80.00.</Alert>
                    )}

                    <SegmentedTabs
                        label="Exam"
                        value={selectedExamName}
                        options={examNameOptions}
                        onChange={(value) => setSelectedExamName(value as ExamName)}
                        helperText="Academic and Core distributions are saved together, but runtime scoring uses only the selected exam's rules."
                    />

                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2.5,
                            gridTemplateColumns: {
                                xs: '1fr',
                                xl: 'repeat(2, minmax(0, 1fr))',
                            },
                        }}
                    >
                        {examTaskCategories.map((category) => {
                            const rules = selectedRules
                                .filter((rule) => rule.sectionCategory === category.id)
                                .sort((left, right) => {
                                    const leftTaskType =
                                        examTaskTypes.find((taskType) => taskType.apiType === left.examTaskType) ?? null;
                                    const rightTaskType =
                                        examTaskTypes.find((taskType) => taskType.apiType === right.examTaskType) ?? null;
                                    const leftLabel = leftTaskType
                                        ? getTaskTypeLabel(leftTaskType, selectedExamName)
                                        : left.examTaskType;
                                    const rightLabel = rightTaskType
                                        ? getTaskTypeLabel(rightTaskType, selectedExamName)
                                        : right.examTaskType;
                                    return leftLabel.localeCompare(rightLabel);
                                });
                            const total = rules.reduce((sum, rule) => sum + rule.allocatedMark, 0);
                            const isExactTotal =
                                Math.abs(total - mockTestSectionAllocationTarget) <
                                mockTestSectionAllocationTolerance;

                            return (
                                <Paper
                                    key={`${selectedExamName}-${category.id}`}
                                    variant="outlined"
                                    sx={{
                                        p: 2.5,
                                        borderRadius: 3,
                                        borderColor: 'rgba(148, 163, 184, 0.2)',
                                        background:
                                            'linear-gradient(180deg, rgba(248,250,252,0.98) 0%, rgba(241,245,249,0.98) 100%)',
                                    }}
                                >
                                    <Stack spacing={2}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                                    {category.label}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                                    {rules.length} question type{rules.length === 1 ? '' : 's'}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                size="small"
                                                label={`${total.toFixed(2)} / 80.00`}
                                                color={isExactTotal ? 'success' : 'warning'}
                                                variant={isExactTotal ? 'filled' : 'outlined'}
                                            />
                                        </Stack>

                                        {rules.length === 0 ? (
                                            <Alert severity="warning">No {category.label.toLowerCase()} distribution is configured.</Alert>
                                        ) : (
                                            <Stack spacing={1.5}>
                                                {rules.map((rule) => {
                                                    const taskType =
                                                        examTaskTypes.find((item) => item.apiType === rule.examTaskType) ?? null;
                                                    const taskLabel = taskType
                                                        ? getTaskTypeLabel(taskType, selectedExamName)
                                                        : formatEnumLabel(rule.examTaskType);

                                                    return (
                                                        <Box
                                                            key={`${rule.examName}-${rule.sectionCategory}-${rule.examTaskType}`}
                                                            sx={{
                                                                display: 'grid',
                                                                gap: 1.5,
                                                                alignItems: 'center',
                                                                gridTemplateColumns: {
                                                                    xs: '1fr',
                                                                    sm: 'minmax(0, 1fr) 150px',
                                                                },
                                                            }}
                                                        >
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>
                                                                {taskLabel}
                                                            </Typography>
                                                            <NumberField
                                                                label="Distribution %"
                                                                value={rule.allocatedMark}
                                                                min={0.01}
                                                                step="0.01"
                                                                onChange={(value) => updateRuleWeight(rule, value ?? 0)}
                                                            />
                                                        </Box>
                                                    );
                                                })}
                                            </Stack>
                                        )}
                                    </Stack>
                                </Paper>
                            );
                        })}
                    </Box>

                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button variant="outlined" onClick={onReset} disabled={!isDirty || isSaving}>
                            Reset
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => void onSave()}
                            disabled={isSaving || !isDirty || !isMockTestConfigValid(draft)}
                        >
                            {config ? 'Save Scoring Config' : 'Create Scoring Config'}
                        </Button>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}

function createDefaultThresholdBand(): ThresholdScoreBand {
    return {
        minInclusive: 0,
        maxInclusive: 0,
        score: 0,
    };
}

function createDefaultWordCountBand(): WordCountScoreBand {
    return {
        minInclusive: 0,
        maxInclusive: 0,
        score: 0,
    };
}

function createDefaultRelativeThresholdBand(): RelativeThresholdScoreBand {
    return {
        thresholdKind: 'Absolute',
        thresholdValue: 0,
        offset: 0,
        divisor: 1,
        score: 0,
    };
}

export function MarkingConfigurationPage() {
    const navigate = useNavigate();
    const notify = useNotify();
    const { section } = useParams<{ section?: string }>();

    const activeSection = getMarkingSection(section);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [taskConfigs, setTaskConfigs] = useState<AdminTaskMarkingConfigDTO[]>([]);
    const [mockTestConfig, setMockTestConfig] = useState<AdminMockTestScoringConfigDTO | null>(null);
    const [taskExamName, setTaskExamName] = useState<ExamName>('Academic');
    const [selectedTaskType, setSelectedTaskType] = useState('');
    const [taskSearch, setTaskSearch] = useState('');
    const [taskDraft, setTaskDraft] = useState<TaskMarkingConfig>(createDefaultTaskDraft('Academic', null));
    const [mockTestDraft, setMockTestDraft] = useState<MockTestScoringConfiguration>(createDefaultMockTestDraft());
    const [isSavingTask, setIsSavingTask] = useState(false);
    const [isSavingMockTest, setIsSavingMockTest] = useState(false);

    const taskConfigMap = useMemo(() => {
        const map = new Map<string, AdminTaskMarkingConfigDTO>();
        for (const config of taskConfigs) {
            map.set(taskKey(config.examName, config.examTaskType), config);
        }
        return map;
    }, [taskConfigs]);

    const taskTypesForExam = useMemo(() => getExamTaskTypesByExamName(taskExamName), [taskExamName]);

    const selectedTaskTypeOption = useMemo(
        () => taskTypesForExam.find((taskType) => taskType.apiType === selectedTaskType) ?? null,
        [selectedTaskType, taskTypesForExam],
    );

    const taskBaseline = useMemo(
        () => getTaskDraftBaseline(taskExamName, selectedTaskType, taskConfigMap),
        [selectedTaskType, taskConfigMap, taskExamName],
    );

    const mockTestBaseline = useMemo(() => getMockTestBaseline(mockTestConfig), [mockTestConfig]);

    const isTaskDirty = useMemo(
        () => JSON.stringify(taskDraft) !== JSON.stringify(taskBaseline),
        [taskBaseline, taskDraft],
    );
    const isMockTestDirty = useMemo(
        () => JSON.stringify(mockTestDraft) !== JSON.stringify(mockTestBaseline),
        [mockTestBaseline, mockTestDraft],
    );
    const hasUnsavedChanges = isTaskDirty || isMockTestDirty;

    const currentExamConfiguredCount = useMemo(
        () => taskTypesForExam.filter((taskType) => taskConfigMap.has(taskKey(taskExamName, taskType.apiType))).length,
        [taskConfigMap, taskExamName, taskTypesForExam],
    );

    const totalTaskCount = taskTypesForExam.length;
    const mockTestRuleCount = mockTestConfig?.configuration.sectionAllocationRules.length ?? mockTestDraft.sectionAllocationRules.length;

    async function loadMarkingData() {
        setIsLoading(true);
        setLoadError('');

        try {
            const [taskConfigList, mockTestScoringConfig] = await Promise.all([
                getAdminMarkingTaskConfigs(),
                getAdminMockTestScoringConfig(),
            ]);

            setTaskConfigs(taskConfigList);
            setMockTestConfig(mockTestScoringConfig);

            setTaskExamName((current) => {
                const nextTaskTypes = getExamTaskTypesByExamName(current);
                if (nextTaskTypes.length > 0) return current;
                return 'Academic';
            });

            setSelectedTaskType((current) => setTaskTypeIfMissing(taskExamName, taskConfigList, current));
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to load marking configuration';
            setTaskConfigs([]);
            setMockTestConfig(null);
            setLoadError(message);
            notify(message, { type: 'error' });
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        void loadMarkingData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (taskTypesForExam.length === 0) {
            setSelectedTaskType('');
            return;
        }

        setSelectedTaskType((current) => setTaskTypeIfMissing(taskExamName, taskConfigs, current));
    }, [taskConfigs, taskExamName, taskTypesForExam.length]);

    useEffect(() => {
        if (!selectedTaskTypeOption) {
            setTaskDraft(createDefaultTaskDraft(taskExamName, null));
            return;
        }

        setTaskDraft(taskBaseline);
    }, [selectedTaskTypeOption, taskBaseline, taskExamName]);

    useEffect(() => {
        setMockTestDraft(mockTestBaseline);
    }, [mockTestBaseline]);

    useEffect(() => {
        if (markingSections.includes(section as MarkingSection)) return;
        navigate('/marking-configuration/task-configs', { replace: true });
    }, [navigate, section]);

    const handleRefresh = () => {
        if (hasUnsavedChanges && !window.confirm('Refresh will discard unsaved changes. Continue?')) {
            return;
        }

        void loadMarkingData();
    };

    const handleTaskExamNameChange = (nextExamName: ExamName) => {
        if (nextExamName === taskExamName) return;
        if (isTaskDirty && !window.confirm('Switching exam name will discard the current task config draft. Continue?')) {
            return;
        }

        setTaskSearch('');
        setTaskExamName(nextExamName);
    };

    const handleSelectTaskType = (taskType: string) => {
        if (taskType === selectedTaskType) return;
        if (isTaskDirty && !window.confirm('Switching task type will discard the current task config draft. Continue?')) {
            return;
        }

        setSelectedTaskType(taskType);
    };

    const handleSaveTask = async () => {
        if (!selectedTaskTypeOption) return;

        setIsSavingTask(true);
        try {
            const savedTaskConfig = await upsertAdminMarkingTaskConfig(
                taskExamName,
                selectedTaskType,
                taskDraft,
            );

            setTaskConfigs((current) => {
                const nextConfigs = current.filter((config) => taskKey(config.examName, config.examTaskType) !== taskKey(taskExamName, selectedTaskType));
                return [...nextConfigs, savedTaskConfig].sort((left, right) => {
                    const leftKey = taskKey(left.examName, left.examTaskType);
                    const rightKey = taskKey(right.examName, right.examTaskType);
                    return leftKey.localeCompare(rightKey);
                });
            });
            setTaskDraft(cloneValue(savedTaskConfig.configuration));
            notify('Task marking config saved', { type: 'info' });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to save task config';
            notify(message, { type: 'error' });
        } finally {
            setIsSavingTask(false);
        }
    };

    const handleSaveMockTest = async () => {
        setIsSavingMockTest(true);
        try {
            const savedConfig = await upsertAdminMockTestScoringConfig(mockTestDraft);
            setMockTestConfig(savedConfig);
            setMockTestDraft(cloneValue(savedConfig.configuration));
            notify('Mock test scoring config saved', { type: 'info' });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to save mock test scoring config';
            notify(message, { type: 'error' });
        } finally {
            setIsSavingMockTest(false);
        }
    };

    return (
        <Card sx={{ borderRadius: 4, boxShadow: '0 24px 80px rgba(15, 23, 42, 0.10)', overflow: 'hidden' }}>
            <Title title="Marking Configuration" />
            {isLoading || isSavingTask || isSavingMockTest ? <LinearProgress /> : null}
            <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                <Stack spacing={3}>
                    <Box
                        sx={{
                            borderRadius: 4,
                            p: { xs: 2.5, md: 3 },
                            color: '#f8fafc',
                            background:
                                'radial-gradient(circle at top right, rgba(56, 189, 248, 0.35) 0%, rgba(15, 23, 42, 0) 34%), linear-gradient(135deg, #0f172a 0%, #111827 55%, #1e293b 100%)',
                            boxShadow: '0 20px 50px rgba(15, 23, 42, 0.18)',
                        }}
                    >
                        <Stack spacing={2}>
                            <Stack
                                direction={{ xs: 'column', lg: 'row' }}
                                spacing={2}
                                justifyContent="space-between"
                                alignItems={{ lg: 'center' }}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        variant="overline"
                                        sx={{
                                            display: 'block',
                                            color: '#7dd3fc',
                                            fontWeight: 800,
                                            letterSpacing: '0.14em',
                                        }}
                                    >
                                        Runtime-backed scoring
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                                        Marking Configuration
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ mt: 1, maxWidth: 900, color: 'rgba(226, 232, 240, 0.88)' }}
                                    >
                                        Edit the same database-backed marking data that the admin controller serves to runtime scoring,
                                        the background mock-test queue consumer, and the mock-test rescoring flow.
                                    </Typography>
                                </Box>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshRoundedIcon />}
                                        onClick={handleRefresh}
                                        sx={{
                                            color: '#e2e8f0',
                                            borderColor: 'rgba(226, 232, 240, 0.4)',
                                            '&:hover': {
                                                borderColor: '#e2e8f0',
                                                backgroundColor: 'rgba(148, 163, 184, 0.12)',
                                            },
                                        }}
                                    >
                                        Refresh Data
                                    </Button>
                                </Stack>
                            </Stack>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        md: 'repeat(2, minmax(0, 1fr))',
                                        xl: 'repeat(4, minmax(0, 1fr))',
                                    },
                                }}
                            >
                                <SummaryCard
                                    label="Task configs"
                                    value={`${taskConfigs.length}`}
                                    helper={`${currentExamConfiguredCount}/${totalTaskCount} configured for ${taskExamName}`}
                                />
                                <SummaryCard
                                    label="Mock test rules"
                                    value={`${mockTestRuleCount}`}
                                    helper="Allocation rows used by mock-test scoring and rescoring"
                                />
                                <SummaryCard
                                    label="Open drafts"
                                    value={`${Number(hasUnsavedChanges)}`}
                                    helper={hasUnsavedChanges ? 'There are unsaved edits on one or more sections' : 'All sections are in sync with the loaded config'}
                                />
                            </Box>
                        </Stack>
                    </Box>

                    {loadError ? <Alert severity="error">{loadError}</Alert> : null}

                    <Tabs
                        value={activeSection}
                        onChange={(_event, nextValue: MarkingSection) => navigate(`/marking-configuration/${nextValue}`)}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                    >
                        <Tab value="task-configs" label={sectionLabels['task-configs']} />
                        <Tab value="mock-test-scoring" label={sectionLabels['mock-test-scoring']} />
                        <Tab value="content-scoring" label={sectionLabels['content-scoring']} />
                    </Tabs>

                    {activeSection === 'task-configs' ? (
                        <TaskConfigTab
                            examName={taskExamName}
                            onExamNameChange={handleTaskExamNameChange}
                            taskTypes={taskTypesForExam}
                            selectedTaskType={selectedTaskType}
                            onSelectTaskType={handleSelectTaskType}
                            taskConfigs={taskConfigs}
                            taskDraft={taskDraft}
                            onTaskDraftChange={setTaskDraft}
                            onTaskSave={handleSaveTask}
                            onTaskReset={() => setTaskDraft(taskBaseline)}
                            isSavingTask={isSavingTask}
                            isTaskDirty={isTaskDirty}
                            taskSearch={taskSearch}
                            onTaskSearchChange={setTaskSearch}
                        />
                    ) : null}

                    {activeSection === 'mock-test-scoring' ? (
                        <MockTestScoringEditor
                            draft={mockTestDraft}
                            onChange={setMockTestDraft}
                            onSave={handleSaveMockTest}
                            onReset={() => setMockTestDraft(mockTestBaseline)}
                            isSaving={isSavingMockTest}
                            isDirty={isMockTestDirty}
                            config={mockTestConfig}
                        />
                    ) : null}

                    {activeSection === 'content-scoring' ? <SemanticContentScoringPoliciesPanel /> : null}
                </Stack>
            </CardContent>
        </Card>
    );
}
